import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AnchoredDialog from '@srm/ui/anchored-dialog';
import { INotification } from '@models/INotification';
import { useUi } from '@srm/ui/provider';
import { RouteNames } from '@/router/router.types';
import { NotificationGroupCounts, NotificationsService } from '@services/NotificationsService';
import Button from '@srm/ui/button';
import {
    MdArrowBack,
    MdChevronRight,
    MdOutlineDelete,
    MdOutlineDescription,
    MdOutlineDoneAll,
    MdOutlineOpenInNew,
    MdOutlineSchedule,
    MdOutlineTune,
    MdSupportAgent,
} from 'react-icons/md';
import NotificationText from '@molecules/NotificationText';
import { computeDateBoundaries, dateBucketOf, groupNotificationsByDate } from '@utils/groupNotificationsByDate';
import { categoryTokenFromTag, isMuteableTag } from '@utils/notificationCategory';
import { snoozeNotificationToReminder } from '@utils/snoozeNotification';
import { plural } from '@srm/ui/lib/pluralize';
import NotificationItemMenu from '@molecules/NotificationItemMenu';
import { useNotificationPreferencesEnabled } from '@hooks/useNotificationPreferencesEnabled';
import SnoozeButton from '@molecules/SnoozeButton/SnoozeButton';
import { NotificationPreferencesService } from '@services/NotificationPreferencesService';
import { sendNotification } from '@srm/ui/lib/notifications';
import NotificationDropdownSkeleton from './NotificationDropdownSkeleton';
import clsx from 'clsx';
import {
    backgroundJobNotificationKind,
    backgroundJobTargetId,
    hasBoundDocumentTarget,
    hasSupportAccessTarget,
    SUPPORT_ACCESS_PATH,
} from '@utils/notificationTarget';
import { IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';
import { backgroundJobsStore } from '@services/backgroundJobs/BackgroundJobsStore';
import {
    BackgroundJobAction,
    createJobActionHandlers,
    openJobLink,
    runJobAction,
} from '@services/backgroundJobs/backgroundJobKinds';
import { useBackgroundJobs, useJobDownloads } from '@services/backgroundJobs/useBackgroundJobs';
import BackgroundJobsSection, { IBackgroundJobsSectionProps } from './BackgroundJobsSection/BackgroundJobsSection';
import JobNotificationActions from './JobNotificationActions/JobNotificationActions';
import './NotificationDropdown.scss';

// Модалка «Отложить» тянет DevExtreme DataGrid (~2 MB vendor-чанк) — грузим
// лениво по факту открытия (симметрично Navbar.tsx).
const MailReminderExecutorModal = React.lazy(() =>
    import('@organisms/actions/docExecutors/MailReminder/MailReminderExecutorModal').then((m) => ({
        default: m.MailReminderExecutorModal,
    })),
);

export interface INotificationDropdownProps {
    /** Кнопка-якорь (Popover.Target). */
    target: React.ReactNode;
    opened: boolean;
    onClose: () => void;
    unreadCount: number;
    /** Инкрементируется при внешних событиях (NewNotification) — soft refresh без очистки списка */
    refreshSignal: number;
    /** Вызывается после локальной мутации (delete / markAsRead), чтобы Navbar обновил badge без ожидания SignalR */
    onLocalMutation: () => void;
    /** Запрос открыть деталь конкретного уведомления (клик по превью-тосту у колокольчика). */
    detailRequest?: { id: string } | null;
    /** Пользователь скрыл итог фоновой задачи в разделе задач. */
    onJobDismissed?: (jobId: string) => void;
}

const PAGE_SIZE = 10;
const DELETE_ANIMATION_MS = 220;
const SCROLL_THRESHOLD = 50;
/** Окно после локальной мутации, в течение которого эхо SignalR игнорируется (список уже обновлён локально). */
const SELF_ECHO_WINDOW_MS = 1500;
/** Дебаунс счётчиков групп — коалесцирует всплеск мутаций (mark-all, серия прочтений) в один запрос. */
const GROUP_COUNTS_DEBOUNCE_MS = 250;

const WEEKDAYS_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/**
 * Время в строке списка — формат зависит от группы (бакета), чтобы не дублировать
 * подпись группы:
 *  - Сегодня / Вчера → только время `18:40` (день уже в заголовке группы);
 *  - На этой / прошлой неделе → день недели + время `Вт, 16:20`;
 *  - Ранее → дата (+ год, если не текущий) + время `12 декабря, 14:35`.
 * Полную дату/время показываем тултипом (title) — см. formatFullDate.
 */
const formatTime = (date: Date | string): string => {
    const d = new Date(date);
    const now = new Date();
    const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    const bucket = dateBucketOf(d, now);

    if (bucket === 'today' || bucket === 'yesterday') {
        return time;
    }
    if (bucket === 'thisWeek' || bucket === 'lastWeek') {
        return `${WEEKDAYS_SHORT[d.getDay()]}, ${time}`;
    }

    const dateOpts: Intl.DateTimeFormatOptions =
        d.getFullYear() === now.getFullYear()
            ? { day: 'numeric', month: 'long' }
            : { day: 'numeric', month: 'long', year: 'numeric' };

    return d.toLocaleDateString('ru-RU', dateOpts) + `, ${time}`;
};

const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

/**
 * Стабильная сортировка «непрочитанные сверху, прочитанные снизу» — порядок по дате внутри
 * сохраняется. Применяется к элементам каждой группы на отображении (см. `groups`), а не к
 * самому `items` — тот держим в серверном порядке ради консистентной пагинации.
 */
const sortUnreadFirst = (list: INotification[]): INotification[] =>
    [...list].sort((a, b) => Number(a.read) - Number(b.read));

/** Развёрнутая дата для горизонтального просмотра (морф детали). */
const formatFullDate = (date: Date | string): string =>
    new Date(date).toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

/**
 * Раздел задач подписан на их состояние сам: шапка и лента не перерисовываются на каждый ход задачи. Открытый раздел
 * показывает ход всех задач — пуш хода дочитывается, пока он открыт.
 */
const BackgroundJobsSlot: FC<Omit<IBackgroundJobsSectionProps, 'jobs' | 'downloadingJobIds'>> = (props) => {
    const { enabled, jobs } = useBackgroundJobs();
    const downloads = useJobDownloads();
    useEffect(() => backgroundJobsStore.viewProgress(), []);
    return enabled === false ? null : <BackgroundJobsSection jobs={jobs} downloadingJobIds={downloads} {...props} />;
};

const NotificationDropdown: FC<INotificationDropdownProps> = ({
    target,
    opened,
    onClose,
    unreadCount,
    refreshSignal,
    onLocalMutation,
    detailRequest,
    onJobDismissed,
}) => {
    const { navigate } = useUi();
    // Киллсвич центра настроек уведомлений: прячет шестерёнку и пункты «Настроить»/«Не уведомлять».
    const prefsEnabled = useNotificationPreferencesEnabled();
    const [items, setItems] = useState<INotification[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [deletingIds, setDeletingIds] = useState<string[]>([]);
    /** Точные счётчики непрочитанных по группам дат (с бэка); null — пока не загружены. */
    const [groupCounts, setGroupCounts] = useState<NotificationGroupCounts | null>(null);
    /** Документ для модалки «Отложить» (создание напоминания); null — модалка закрыта. */
    const [snoozeDocKey, setSnoozeDocKey] = useState<string | null>(null);
    const [snoozeText, setSnoozeText] = useState('');
    /** Оверлей-лоадер на дропдауне во время массовых операций (прочитать все / отметить группу). */
    const [isBusy, setIsBusy] = useState(false);
    /** Завершён ли первый initialLoad — чтобы не мелькало «нет уведомлений» до первой загрузки. */
    const [hasLoaded, setHasLoaded] = useState(false);

    /** Уведомление, открытое в горизонтальном просмотре. Данные держим и во время обратного морфа. */
    const [active, setActive] = useState<INotification | null>(null);
    /** Управляет морфом: true — детальный (горизонтальный) вид, false — список. */
    const [detailOpen, setDetailOpen] = useState(false);
    /** Измеренная высота детального пейна (px) для вертикального морфа. */
    const [detailHeight, setDetailHeight] = useState<number | undefined>(undefined);
    /** Инкремент на каждый openDetail — триггерит синхронный замер+открытие (даже для того же уведомления). */
    const [openToken, setOpenToken] = useState(0);
    /** Высота раздела «Фоновые задачи»: прибавляется к высоте списка, чтобы раздел не съедал ленту. */
    const [jobsHeight, setJobsHeight] = useState(0);

    const listRef = useRef<HTMLDivElement | null>(null);
    /** scrollTop списка на входе в детальный вид — вернуть ту же позицию на «назад».
     *  Прочитанное при открытии уезжает в конец группы (sortUnreadFirst); без этого
     *  возврат фокуса на элемент «улетел бы» скроллом вниз, к его новой позиции. */
    const listScrollTopRef = useRef(0);
    const backButtonRef = useRef<HTMLButtonElement | null>(null);
    const detailPaneRef = useRef<HTMLDivElement | null>(null);
    /** Флаг отложенного открытия: контент детали уже отрисован, ждём замер высоты. */
    const pendingOpenRef = useRef(false);

    /** Таймауты текущих delete-анимаций — ключ: id уведомления */
    const deleteTimeoutsRef = useRef<Record<string, number>>({});
    /** Монотонный счётчик запросов — ответ с устаревшим version отбрасывается */
    const requestVersionRef = useRef(0);
    const hasMoreRef = useRef(true);
    const itemsLengthRef = useRef(0);
    /** Блокирует повторный initialLoad / loadMore (НЕ используется для softRefresh и backfill) */
    const loadingRef = useRef(false);
    /** Предыдущее значение opened — для синхронного детекта «только что открылся» (см. justOpened). */
    const prevIsOpenRef = useRef(opened);
    /** Отложенный refresh — применяется после завершения всех delete-анимаций */
    const pendingRefreshRef = useRef<number | null>(null);
    /** ID элементов, удалённых локально в текущей сессии dropdown — фильтруются из всех ответов */
    const locallyDeletedIdsRef = useRef<Set<string>>(new Set());
    /** ID элементов, помеченных прочитанными локально — форсируют read: true при получении данных с сервера */
    const locallyMarkedReadIdsRef = useRef<Set<string>>(new Set());
    /** Время последней локальной мутации (мс) — гасит эхо-softRefresh от собственного действия. */
    const lastLocalMutationAtRef = useRef(0);
    /** Последний refreshSignal, который уже покрыт запросом (initialLoad при открытии или softRefresh). */
    const handledRefreshSignalRef = useRef(refreshSignal);
    /** Актуальный refreshSignal для эффекта открытия (сам он на сигнал перезапускаться не должен). */
    const refreshSignalRef = useRef(refreshSignal);
    /** Таймер дебаунса счётчиков групп. */
    const groupCountsTimerRef = useRef<number | null>(null);

    hasMoreRef.current = hasMore;
    itemsLengthRef.current = items.length;
    refreshSignalRef.current = refreshSignal;

    // «Только что открылся» (этот рендер). Контент Popover монтируется с прежним state раньше, чем
    // отработают эффекты, поэтому на рендере открытия показываем лоадер, а не пустое состояние.
    // prevIsOpenRef обновляем в эффекте (не в рендере) — корректно при StrictMode double-render.
    const justOpened = opened && !prevIsOpenRef.current;

    /** Превью списка считаем один раз на смену items — иначе stripHtml дёргал бы DOM на каждый кадр морфа. */
    const previews = useMemo(() => {
        const map = new Map<string, string>();
        items.forEach((n) => map.set(n.id, stripHtml(n.text)));
        return map;
    }, [items]);

    /**
     * Группировка по датам для разделителей. now фиксируется на момент пересчёта (смена items).
     * Внутри каждой группы — непрочитанные сверху, прочитанные снизу. Сортировка — только на
     * отображении: сам `items` остаётся в серверном порядке (по дате), иначе пагинация по
     * `skip = items.length` и добор после удаления (`fetchDropdownPage(remainingCount, 1)`,
     * адресуется по позиции серверного порядка) брали бы не те элементы.
     */
    /** Задачи, об итоге которых уже есть уведомление (в ленте или открытой детали): их строка в разделе — дубль. */
    const notifiedJobIds = useMemo(() => {
        const ids = new Set<string>();
        [...items, active].forEach((notification) => {
            const jobId = backgroundJobTargetId(notification);
            if (jobId) ids.add(jobId);
        });
        return ids;
    }, [items, active]);

    const groups = useMemo(
        () =>
            groupNotificationsByDate(items, new Date()).map((group) => ({
                ...group,
                items: sortUnreadFirst(group.items),
            })),
        [items],
    );

    /** Форсирует read: true для элементов, помеченных прочитанными локально */
    const applyLocalReadOverrides = (data: INotification[]): INotification[] =>
        data.map((n) => (locallyMarkedReadIdsRef.current.has(n.id) ? { ...n, read: true } : n));

    /**
     * Завершение цикла загрузки списка: гасит скелетон и разрешает пустое состояние.
     * Зовёт только владелец актуальной версии запроса — устаревший ответ не имеет права
     * объявить список загруженным, иначе на месте ещё не пришедших данных мелькнёт
     * «Нет уведомлений». Владение переходит к запросу, который забрал версию себе.
     */
    const settleLoaded = useCallback((version: number) => {
        if (requestVersionRef.current !== version) return;
        setIsInitialLoading(false);
        setHasLoaded(true);
    }, []);

    /** Hard reset — только при первом открытии dropdown */
    const initialLoad = useCallback(() => {
        locallyDeletedIdsRef.current.clear();
        locallyMarkedReadIdsRef.current.clear();
        requestVersionRef.current += 1;
        const version = requestVersionRef.current;
        loadingRef.current = true;
        setItems([]);
        setHasMore(true);
        setDeletingIds([]);
        setIsInitialLoading(true);
        setIsPageLoading(false);
        setHasLoaded(false);

        NotificationsService.fetchDropdownPage(0, PAGE_SIZE)
            .then(({ data, totalCount }) => {
                if (requestVersionRef.current !== version) return;
                setItems(applyLocalReadOverrides(data));
                setHasMore(data.length > 0 && PAGE_SIZE < totalCount);
            })
            .catch(() => {})
            .finally(() => {
                loadingRef.current = false;
                settleLoaded(version);
            });
    }, [settleLoaded]);

    /** Soft refresh — fetch без очистки items, без loader. Для внешних событий (NewNotification). */
    const softRefresh = useCallback(() => {
        requestVersionRef.current += 1;
        const version = requestVersionRef.current;
        const count = Math.max(PAGE_SIZE, itemsLengthRef.current);

        NotificationsService.fetchDropdownPage(0, count)
            .then(({ data, totalCount }) => {
                if (requestVersionRef.current !== version) return;
                const filtered = applyLocalReadOverrides(data.filter((n) => !locallyDeletedIdsRef.current.has(n.id)));
                setItems(filtered);
                setHasMore(data.length > 0 && count < totalCount);
            })
            .catch(() => {})
            // softRefresh мог перехватить версию у ещё летящего initialLoad — тогда гасить
            // скелетон обязан он, иначе загрузка «зависнет» до закрытия дропдауна.
            .finally(() => settleLoaded(version));
    }, [settleLoaded]);

    /** Load more — scroll pagination с bottom spinner */
    const loadMore = useCallback(() => {
        if (loadingRef.current || !hasMoreRef.current) return;
        requestVersionRef.current += 1;
        const version = requestVersionRef.current;
        const skip = itemsLengthRef.current;
        loadingRef.current = true;
        setIsPageLoading(true);

        NotificationsService.fetchDropdownPage(skip, PAGE_SIZE)
            .then(({ data, totalCount }) => {
                if (requestVersionRef.current !== version) return;
                setItems((prev) => {
                    const existingIds = new Set(prev.map((n) => n.id));
                    const newItems = applyLocalReadOverrides(
                        data
                            .filter((n) => !existingIds.has(n.id))
                            .filter((n) => !locallyDeletedIdsRef.current.has(n.id)),
                    );
                    return [...prev, ...newItems];
                });
                setHasMore(data.length > 0 && skip + PAGE_SIZE < totalCount);
            })
            .catch(() => {})
            .finally(() => {
                loadingRef.current = false;
                setIsPageLoading(false);
            });
    }, []);

    /**
     * Запросить точные счётчики по группам дат (не зависят от пагинации). Дебаунсим: серия
     * мутаций (mark-all, быстрые прочтения) и эхо SignalR коалесцируются в один запрос.
     */
    const loadGroupCounts = useCallback(() => {
        if (groupCountsTimerRef.current !== null) {
            window.clearTimeout(groupCountsTimerRef.current);
        }
        groupCountsTimerRef.current = window.setTimeout(() => {
            groupCountsTimerRef.current = null;
            NotificationsService.fetchDropdownGroupCounts(computeDateBoundaries(new Date()))
                .then(setGroupCounts)
                .catch(() => {});
        }, GROUP_COUNTS_DEBOUNCE_MS);
    }, []);

    /**
     * Локальная мутация (прочтение/удаление/откладывание): помечаем время — чтобы эхо
     * SignalR от собственного действия не вызвало лишний softRefresh всего списка — и зовём
     * onLocalMutation (обновление бейджа Navbar, который сам решает, нужен ли запрос).
     */
    const markLocalMutation = useCallback(() => {
        lastLocalMutationAtRef.current = Date.now();
        onLocalMutation();
    }, [onLocalMutation]);

    // Точные счётчики групп: при открытии и при любом изменении числа непрочитанных (мутации/новое).
    useEffect(() => {
        if (opened) {
            loadGroupCounts();
        }
    }, [opened, unreadCount, loadGroupCounts]);

    // Загрузить при открытии. useLayoutEffect (а не useEffect) — чтобы переход в loading-состояние
    // (setIsInitialLoading(true) внутри initialLoad) применился ДО первого paint: иначе на повторном
    // открытии успевает мелькнуть «нет уведомлений» от прежнего состояния, пока идёт первый запрос.
    useLayoutEffect(() => {
        if (opened) {
            pendingRefreshRef.current = null;
            // Сигналы, накопившиеся пока dropdown был закрыт, покрыты этой загрузкой:
            // initialLoad тянет первую страницу заново. Без этого эффект refreshSignal
            // сразу же дал бы второй запрос (он зависит от opened), тот перехватил бы
            // версию у initialLoad — и на кадры до своего ответа показал бы пустой список.
            handledRefreshSignalRef.current = refreshSignalRef.current;
            // Каждое открытие dropdown стартует со списка — сбрасываем морф.
            setDetailOpen(false);
            setActive(null);
            initialLoad();
        }
    }, [opened, initialLoad]);

    // Закрытие dropdown — свернуть детальный вид, чтобы не «мигал» при следующем открытии.
    useEffect(() => {
        if (!opened) {
            pendingOpenRef.current = false;
            setDetailOpen(false);
            setActive(null);
            // Сброс флага загрузки — чтобы при следующем открытии «нет уведомлений» не мелькал до initialLoad.
            setHasLoaded(false);
        }
    }, [opened]);

    // Запоминаем предыдущее opened после рендера (для justOpened) — в эффекте, а не в рендере.
    useEffect(() => {
        prevIsOpenRef.current = opened;
    }, [opened]);

    // При входе в детальный вид фокус уводим на кнопку «назад» (клавиатура + Esc/Enter).
    useEffect(() => {
        if (detailOpen) {
            backButtonRef.current?.focus();
        }
    }, [detailOpen]);

    /** Высота детального пейна (offsetHeight). */
    const measureDetailHeight = useCallback(() => {
        if (detailPaneRef.current) {
            setDetailHeight(detailPaneRef.current.offsetHeight);
        }
    }, []);

    // Синхронное открытие. На клик мы лишь выставляем active (контент детали рендерится,
    // визуально всё ещё список). Здесь, до отрисовки, меряем высоту и в ОДНОМ апдейте
    // включаем detailOpen (→ ширина) и detailHeight (→ высота) — обе оси морфа стартуют
    // на одном кадре, без рассинхрона «друг за другом».
    useLayoutEffect(() => {
        if (pendingOpenRef.current && detailPaneRef.current) {
            pendingOpenRef.current = false;
            setDetailHeight(detailPaneRef.current.offsetHeight);
            setDetailOpen(true);
        }
    }, [openToken]);

    // Догонять реальные изменения высоты детали (reflow HTML/шрифтов, resize окна) — пока открыта.
    useEffect(() => {
        const pane = detailPaneRef.current;
        if (!pane || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => {
            if (detailOpen) {
                measureDetailHeight();
            }
        });
        observer.observe(pane);
        return () => observer.disconnect();
    }, [detailOpen, measureDetailHeight]);

    // Внешний refresh (NewNotification) — soft refresh или defer.
    // Эхо SignalR (NotificationsChanged) от собственной мутации игнорируем: список уже обновлён
    // локально, перезагружать его целиком незачем. Изменения вне self-echo окна — внешние, рефрешим.
    useEffect(() => {
        if (!opened || refreshSignal === 0) return;
        // Реагируем только на НОВЫЙ сигнал. Сам факт открытия сюда не считается: свежие данные
        // уже тянет initialLoad, а второй запрос лишь дублировал бы GET и ломал бы состояние загрузки.
        if (handledRefreshSignalRef.current === refreshSignal) return;
        handledRefreshSignalRef.current = refreshSignal;
        if (Date.now() - lastLocalMutationAtRef.current < SELF_ECHO_WINDOW_MS) return;

        if (Object.keys(deleteTimeoutsRef.current).length > 0) {
            pendingRefreshRef.current = refreshSignal;
        } else {
            softRefresh();
        }
    }, [refreshSignal, opened, softRefresh]);

    // Cleanup timeouts при unmount
    useEffect(() => {
        const timeouts = deleteTimeoutsRef.current;
        return () => {
            Object.values(timeouts).forEach((t) => window.clearTimeout(t));
            if (groupCountsTimerRef.current !== null) {
                window.clearTimeout(groupCountsTimerRef.current);
            }
        };
    }, []);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const el = e.currentTarget;
            if (
                el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD &&
                hasMoreRef.current &&
                !loadingRef.current
            ) {
                loadMore();
            }
        },
        [loadMore],
    );

    const handleViewAll = () => {
        onClose();
        navigate('/notification');
    };

    const handleOpenSettings = () => {
        onClose();
        navigate(RouteNames.SETTINGS_NOTIFICATIONS);
    };

    /** «Не уведомлять по документу» из меню уведомления: глушим связанный документ. */
    const handleMuteDocument = (e: React.MouseEvent, notification: INotification) => {
        e.preventDefault();
        e.stopPropagation();
        NotificationsService.getBoundDocumentKey(notification.id)
            .then((res) => {
                if (!res.data) return;
                NotificationPreferencesService.mute(res.data)
                    .then(() =>
                        sendNotification({
                            title: 'Готово',
                            message: 'Уведомления по документу отключены',
                            variant: 'green',
                        }),
                    )
                    .catch(() =>
                        sendNotification({
                            title: 'Ошибка',
                            message: 'Не удалось отключить уведомления по документу',
                            variant: 'red',
                        }),
                    );
            })
            .catch(() => {});
    };

    /** Пометить прочитанным без UI-события (при открытии в детальном виде). */
    const markReadSilently = useCallback(
        (notification: INotification) => {
            if (notification.read) return;
            NotificationsService.markAsRead([notification.id])
                .then(() => {
                    markLocalMutation();
                    locallyMarkedReadIdsRef.current.add(notification.id);
                    setItems((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
                })
                .catch(() => {});
        },
        [markLocalMutation],
    );

    /** Морф в горизонтальный просмотр уведомления. Само открытие (detailOpen) и замер
     *  высоты делаются синхронно в layout-эффекте по openToken — обе оси стартуют разом. */
    const openDetail = useCallback(
        (notification: INotification) => {
            // Запоминаем позицию скролла ДО markReadSilently — тот пересортирует список
            // (прочитанное вниз группы) и валидный «до прочтения» scrollTop будет потерян.
            listScrollTopRef.current = listRef.current?.scrollTop ?? 0;
            pendingOpenRef.current = true;
            setActive(notification);
            setOpenToken((t) => t + 1);
            markReadSilently(notification);
        },
        [markReadSilently],
    );

    // Открыть деталь конкретного уведомления по запросу из превью-тоста (клик по миниатюре).
    // Каждый запрос — новый объект; обрабатываем один раз (по идентичности), при необходимости дотягиваем по id.
    const consumedDetailReqRef = useRef<unknown>(null);
    useEffect(() => {
        if (!detailRequest || !opened || consumedDetailReqRef.current === detailRequest) return;
        consumedDetailReqRef.current = detailRequest;
        const found = items.find((n) => n.id === detailRequest.id);
        if (found) {
            openDetail(found);
        } else {
            NotificationsService.fetchById(detailRequest.id)
                .then((n) => {
                    if (n) openDetail(n);
                })
                .catch(() => {});
        }
    }, [detailRequest, opened, items, openDetail]);

    /** Обратный морф к списку. Данные active не сбрасываем — нужны для анимации выхода. */
    const handleBack = useCallback(() => {
        const id = active?.id;
        setDetailOpen(false);
        // Когда морф завершится: вернуть позицию скролла, что была до прочтения (прочитанное
        // уехало вниз группы), и фокус на исходный элемент — но с preventScroll, иначе фокус
        // сам скроллит к перемещённому элементу вниз списка.
        window.setTimeout(() => {
            if (listRef.current) {
                listRef.current.scrollTop = listScrollTopRef.current;
            }
            if (id) {
                const item = listRef.current?.querySelector<HTMLElement>(`a[href="/notification/${id}"]`);
                item?.focus({ preventScroll: true });
            }
        }, 0);
    }, [active]);

    // Esc в детальном виде — назад к списку. Перехватываем в capture-фазе, чтобы
    // опередить window-listener AnchoredDialog (он закрыл бы весь dropdown).
    useEffect(() => {
        if (!detailOpen) return undefined;
        const onKeyDown = (e: KeyboardEvent) => {
            // Вложенные сведения о ссылке закрываются первыми, сохраняя открытым уведомление.
            if (e.target instanceof Element && e.target.closest('[data-notification-overlay]')) return;
            if (e.key === 'Escape') {
                e.stopPropagation();
                handleBack();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [detailOpen, handleBack]);

    const handleNotificationClick = (
        e: React.MouseEvent<HTMLAnchorElement>,
        notification: INotification,
        isAnimating: boolean,
    ) => {
        if (isAnimating) {
            e.preventDefault();
            return;
        }
        // Модифицированный/не-основной клик — отдаём управление браузеру (открыть в новой вкладке/окне)
        if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
            return;
        }
        e.preventDefault();
        openDetail(notification);
    };

    /** Открыть уведомление целиком на странице /notification/:id. */
    const handleOpenFull = () => {
        if (!active) return;
        onClose();
        navigate(`/notification/${active.id}`);
    };

    /** Удаление из детального вида: убрать элемент и свернуться обратно к списку. */
    const handleDetailDelete = () => {
        if (!active) return;
        const id = active.id;
        NotificationsService.delete([id])
            .then(() => {
                onLocalMutation();
                loadGroupCounts();
                locallyDeletedIdsRef.current.add(id);
                setItems((prev) => prev.filter((n) => n.id !== id));
                handleBack();
            })
            .catch(() => {});
    };

    const handleOpenDocument = (e: React.MouseEvent, notification: INotification) => {
        e.preventDefault();
        e.stopPropagation();
        if (!hasBoundDocumentTarget(notification)) return;

        if (!notification.read) {
            NotificationsService.markAsRead([notification.id])
                .then(() => markLocalMutation())
                .catch(() => {});
        }
        NotificationsService.getBoundDocumentKey(notification.id).then((res) => {
            if (res.data) {
                onClose();
                navigate(RouteNames.DOCUMENT.replace(':id', res.data));
            }
        });
    };

    /** Уведомление о доступе поддержки: раздел «Доступ поддержки» в «Моих настройках». */
    const handleOpenSupportAccess = () => {
        if (!active) return;
        if (!active.read) {
            NotificationsService.markAsRead([active.id])
                .then(() => markLocalMutation())
                .catch(() => {});
        }
        onClose();
        navigate(SUPPORT_ACCESS_PATH);
    };

    const handleCancelJob = useCallback((jobId: string) => backgroundJobsStore.cancel(jobId), []);

    const handleDismissJob = (jobId: string) => {
        backgroundJobsStore.dismiss(jobId);
        onJobDismissed?.(jobId);
    };

    const openDocumentById = (docId: string) => {
        onClose();
        navigate(RouteNames.DOCUMENT.replace(':id', docId));
    };

    const handleJobAction = (action: BackgroundJobAction, job: IBackgroundJob) =>
        runJobAction(
            action,
            job.jobId,
            createJobActionHandlers({ store: backgroundJobsStore, close: onClose, navigate }),
            job.kind,
        );

    const markActiveRead = () => {
        if (active && !active.read) {
            NotificationsService.markAsRead([active.id])
                .then(() => markLocalMutation())
                .catch(() => {});
        }
    };

    /** Документ задачи из детали уведомления о её итоге. */
    const handleOpenJobNotificationDocument = (docId: string) => {
        markActiveRead();
        openDocumentById(docId);
    };

    /** Страница результата задачи из детали уведомления о её итоге. */
    const handleOpenJobNotificationLink = (href: string) => {
        markActiveRead();
        onClose();
        openJobLink(href, navigate);
    };

    /** Завершение delete-анимации: убрать элемент, обработать deferred refresh, добрать хвост */
    const finishDelete = useCallback(
        (id: string) => {
            setDeletingIds((prev) => prev.filter((d) => d !== id));
            setItems((prev) => prev.filter((n) => n.id !== id));
            delete deleteTimeoutsRef.current[id];

            // Если был отложенный refresh и это последняя анимация — soft refresh
            if (Object.keys(deleteTimeoutsRef.current).length === 0 && pendingRefreshRef.current !== null) {
                pendingRefreshRef.current = null;
                softRefresh();
                return;
            }

            // Добрать ровно 1 элемент тихо — без loader, без loadingRef
            if (hasMoreRef.current) {
                const remainingCount = itemsLengthRef.current - 1;
                requestVersionRef.current += 1;
                const version = requestVersionRef.current;

                NotificationsService.fetchDropdownPage(remainingCount, 1)
                    .then(({ data, totalCount }) => {
                        if (requestVersionRef.current !== version) return;
                        setItems((prev) => {
                            const existingIds = new Set(prev.map((n) => n.id));
                            const newItems = applyLocalReadOverrides(
                                data
                                    .filter((n) => !existingIds.has(n.id))
                                    .filter((n) => !locallyDeletedIdsRef.current.has(n.id)),
                            );
                            return [...prev, ...newItems];
                        });
                        setHasMore(totalCount > remainingCount + 1);
                    })
                    .catch(() => {})
                    .finally(() => settleLoaded(version));
            }
        },
        [softRefresh, settleLoaded],
    );

    const handleMarkAsRead = (e: React.MouseEvent, notification: INotification) => {
        e.preventDefault();
        e.stopPropagation();
        if (notification.read) return;

        NotificationsService.markAsRead([notification.id])
            .then(() => {
                markLocalMutation();
                locallyMarkedReadIdsRef.current.add(notification.id);
                setItems((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
            })
            .catch(() => {});
    };

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        e.stopPropagation();

        if (deletingIds.includes(id)) {
            return;
        }

        NotificationsService.delete([id])
            .then(() => {
                onLocalMutation();
                loadGroupCounts();
                locallyDeletedIdsRef.current.add(id);
                setDeletingIds((prev) => [...prev, id]);
                deleteTimeoutsRef.current[id] = window.setTimeout(() => {
                    finishDelete(id);
                }, DELETE_ANIMATION_MS);
            })
            .catch(() => {});
    };

    /** «Отложить → Выбрать время…» — открыть окно создания напоминания по документу. */
    const openSnoozeModal = (notification: INotification) => {
        if (!hasBoundDocumentTarget(notification)) return;

        setSnoozeText(stripHtml(notification.text));
        NotificationsService.getBoundDocumentKey(notification.id)
            .then((res) => {
                if (res.data) {
                    setSnoozeDocKey(res.data);
                }
            })
            .catch(() => {});
    };

    /** «Отложить → пресет» — создать напоминание на дату + локально пометить прочитанным. */
    const handleSnoozePreset = (notification: INotification, date: Date) => {
        if (!hasBoundDocumentTarget(notification)) return;

        snoozeNotificationToReminder(notification.id, stripHtml(notification.text), date)
            .then(() => {
                markLocalMutation();
                locallyMarkedReadIdsRef.current.add(notification.id);
                setItems((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
            })
            .catch(() => {});
    };

    /** Прочитать все — пометить весь список прочитанным. */
    const handleMarkAllAsRead = () => {
        if (unreadCount === 0) return;
        setIsBusy(true);
        NotificationsService.markAllAsRead()
            .then(() => {
                markLocalMutation();
                setItems((prev) =>
                    prev.map((n) => {
                        locallyMarkedReadIdsRef.current.add(n.id);
                        return n.read ? n : { ...n, read: true };
                    }),
                );
            })
            .catch(() => {})
            .finally(() => setIsBusy(false));
    };

    /** Отметить прочитанной всю группу (по наведению на её заголовок). */
    const handleMarkGroupAsRead = (e: React.MouseEvent, groupItems: INotification[]) => {
        e.preventDefault();
        e.stopPropagation();
        const ids = groupItems.filter((n) => !n.read).map((n) => n.id);
        if (ids.length === 0) return;
        setIsBusy(true);
        NotificationsService.markAsRead(ids)
            .then(() => {
                markLocalMutation();
                ids.forEach((id) => locallyMarkedReadIdsRef.current.add(id));
                setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)));
            })
            .catch(() => {})
            .finally(() => setIsBusy(false));
    };

    /** Категория для предпросмотра (детальный вид) — чип рядом с датой. */
    const detailCat = categoryTokenFromTag(active?.tag);
    const DetailCatIcon = detailCat?.icon;
    const detailJobId = backgroundJobTargetId(active);
    const detailJobKind = backgroundJobNotificationKind(active);

    const viewportStyle: React.CSSProperties = {
        ...(jobsHeight > 0 ? { '--ndx-jobs-h': `${jobsHeight}px` } : null),
        ...(detailOpen && detailHeight ? { height: detailHeight } : null),
    } as React.CSSProperties;

    return (
        <>
            <AnchoredDialog
                align="right"
                target={target}
                className={clsx('rf-notification-dropdown', detailOpen && 'rf-notification-dropdown--detail')}
                opened={opened}
                onClose={onClose}
                // Оболочка — fit-content: ширину морфит __viewport (транзишен на корне
                // невозможен — Mantine Transition ставит inline transition-property).
                widthMode="content"
                variant="menu"
            >
                <div className="rf-notification-dropdown__viewport" style={viewportStyle}>
                    {/* ── Пейн «Список» ───────────────────────────────────────── */}
                    <div
                        className="rf-notification-dropdown__pane rf-notification-dropdown__pane--list"
                        aria-hidden={detailOpen}
                    >
                        <div className="rf-notification-dropdown__header">
                            <span className="rf-notification-dropdown__title">
                                Уведомления
                                {unreadCount > 0 && (
                                    <span className="rf-notification-dropdown__header-count">{unreadCount}</span>
                                )}
                            </span>
                            <div className="rf-notification-dropdown__header-actions">
                                <button
                                    type="button"
                                    className="rf-notification-dropdown__header-action"
                                    title="Отметить все прочитанными"
                                    aria-label="Отметить все уведомления прочитанными"
                                    disabled={unreadCount === 0}
                                    tabIndex={detailOpen ? -1 : undefined}
                                    onClick={handleMarkAllAsRead}
                                >
                                    <MdOutlineDoneAll size="18" />
                                </button>
                                {prefsEnabled && (
                                    <button
                                        type="button"
                                        className="rf-notification-dropdown__header-action"
                                        title="Настройки уведомлений"
                                        aria-label="Настройки уведомлений"
                                        tabIndex={detailOpen ? -1 : undefined}
                                        onClick={handleOpenSettings}
                                    >
                                        <MdOutlineTune size="18" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <BackgroundJobsSlot
                            notifiedJobIds={notifiedJobIds}
                            inactive={detailOpen}
                            onCancel={handleCancelJob}
                            onAction={handleJobAction}
                            onDismiss={handleDismissJob}
                            onHeightChange={setJobsHeight}
                        />
                        <div className="rf-notification-dropdown__list" ref={listRef} onScroll={handleScroll}>
                            {items.length === 0 && hasLoaded && !isInitialLoading && !justOpened ? (
                                <div className="rf-notification-dropdown__empty">
                                    <span className="rf-notification-dropdown__empty-icon" aria-hidden="true">
                                        <MdOutlineDoneAll size="30" />
                                    </span>
                                    <span className="rf-notification-dropdown__empty-title">Нет уведомлений</span>
                                    <span className="rf-notification-dropdown__empty-sub">
                                        Здесь появятся согласования, задачи, сроки и напоминания по вашим документам.
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {groups.map((group) => {
                                        const unreadInGroup = group.items.filter((n) => !n.read).length;
                                        // Точные счётчики с бэка (не зависят от пагинации); до загрузки — по загруженным.
                                        const bucketCounts = groupCounts?.[group.key];
                                        const groupUnread = bucketCounts ? bucketCounts.unread : unreadInGroup;
                                        const groupTotal = bucketCounts ? bucketCounts.total : group.items.length;
                                        // Единственная группа «Ранее» — заголовок избыточен (всё и так «ранее»).
                                        const showGroupHeader = !(group.key === 'earlier' && groups.length === 1);
                                        return (
                                            <React.Fragment key={group.key}>
                                                {showGroupHeader && (
                                                    <div className="rf-notification-dropdown__group">
                                                        <span className="rf-notification-dropdown__group-label">
                                                            {group.label}
                                                        </span>
                                                        {groupUnread > 0 ? (
                                                            <span className="rf-notification-dropdown__group-count rf-notification-dropdown__group-count--unread">
                                                                {groupUnread}{' '}
                                                                {plural(groupUnread, ['новое', 'новых', 'новых'])}
                                                            </span>
                                                        ) : groupTotal > 0 ? (
                                                            <span className="rf-notification-dropdown__group-count">
                                                                {groupTotal}
                                                            </span>
                                                        ) : null}
                                                        <span
                                                            className="rf-notification-dropdown__group-rule"
                                                            aria-hidden="true"
                                                        />
                                                        {unreadInGroup > 0 && (
                                                            <button
                                                                type="button"
                                                                className="rf-notification-dropdown__group-mark"
                                                                title="Отметить эту группу прочитанной"
                                                                tabIndex={detailOpen ? -1 : undefined}
                                                                onClick={(e) => handleMarkGroupAsRead(e, group.items)}
                                                            >
                                                                Отметить
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {group.items.map((notification) => {
                                                    const isDeleting = deletingIds.includes(notification.id);
                                                    const hasBoundDocument = hasBoundDocumentTarget(notification);
                                                    const cat = categoryTokenFromTag(notification.tag);
                                                    const CatIcon = cat?.icon;

                                                    return (
                                                        <a
                                                            key={notification.id}
                                                            href={`/notification/${notification.id}`}
                                                            className={clsx(
                                                                'rf-notification-dropdown__item',
                                                                !notification.read &&
                                                                    'rf-notification-dropdown__item--unread',
                                                                isDeleting &&
                                                                    'rf-notification-dropdown__item--deleting',
                                                            )}
                                                            aria-disabled={isDeleting}
                                                            tabIndex={detailOpen ? -1 : undefined}
                                                            onClick={(e) =>
                                                                handleNotificationClick(e, notification, isDeleting)
                                                            }
                                                        >
                                                            <div className="rf-notification-dropdown__item-content">
                                                                <div className="rf-notification-dropdown__item-header">
                                                                    <span
                                                                        className={clsx(
                                                                            'rf-notification-dropdown__item-dot',
                                                                            notification.read &&
                                                                                'rf-notification-dropdown__item-dot--hidden',
                                                                        )}
                                                                        style={
                                                                            !notification.read && cat
                                                                                ? { background: cat.color }
                                                                                : undefined
                                                                        }
                                                                        aria-hidden="true"
                                                                    />
                                                                    <span className="rf-notification-dropdown__item-subject">
                                                                        {notification.subject}
                                                                    </span>
                                                                </div>
                                                                <div className="rf-notification-dropdown__item-preview">
                                                                    {previews.get(notification.id)}
                                                                </div>
                                                                <div className="rf-notification-dropdown__item-footer">
                                                                    <div className="rf-notification-dropdown__item-meta">
                                                                        {cat && CatIcon && (
                                                                            <span
                                                                                className="rf-notification-dropdown__item-cat"
                                                                                style={{
                                                                                    color: cat.color,
                                                                                    background: `color-mix(in srgb, ${cat.color} 6%, transparent)`,
                                                                                    border: `1px solid color-mix(in srgb, ${cat.color} 26%, transparent)`,
                                                                                }}
                                                                            >
                                                                                <CatIcon size="12" />
                                                                                {cat.label}
                                                                            </span>
                                                                        )}
                                                                        <span
                                                                            className="rf-notification-dropdown__item-time"
                                                                            title={formatFullDate(
                                                                                notification.sendedAt,
                                                                            )}
                                                                        >
                                                                            {formatTime(notification.sendedAt)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="rf-notification-dropdown__item-actions">
                                                                        {hasBoundDocument && (
                                                                            <button
                                                                                className="rf-notification-dropdown__item-action"
                                                                                title="Открыть документ"
                                                                                aria-label="Открыть документ"
                                                                                type="button"
                                                                                disabled={isDeleting}
                                                                                onClick={(e) =>
                                                                                    handleOpenDocument(e, notification)
                                                                                }
                                                                            >
                                                                                <MdOutlineDescription size="16" />
                                                                            </button>
                                                                        )}
                                                                        {hasBoundDocument && (
                                                                            <SnoozeButton
                                                                                buttonClassName="rf-notification-dropdown__item-action"
                                                                                title="Отложить — напомнить позже"
                                                                                align="right"
                                                                                onPickPreset={(date) =>
                                                                                    handleSnoozePreset(
                                                                                        notification,
                                                                                        date,
                                                                                    )
                                                                                }
                                                                                onPickCustom={() =>
                                                                                    openSnoozeModal(notification)
                                                                                }
                                                                            />
                                                                        )}
                                                                        <NotificationItemMenu
                                                                            buttonClassName="rf-notification-dropdown__item-action"
                                                                            canMute={
                                                                                prefsEnabled &&
                                                                                hasBoundDocument &&
                                                                                isMuteableTag(notification.tag)
                                                                            }
                                                                            categoryLabel={
                                                                                prefsEnabled ? cat?.label : null
                                                                            }
                                                                            isRead={notification.read}
                                                                            disabled={isDeleting}
                                                                            onMarkRead={(e) =>
                                                                                handleMarkAsRead(e, notification)
                                                                            }
                                                                            onMute={(e) =>
                                                                                handleMuteDocument(e, notification)
                                                                            }
                                                                            onConfigure={() => handleOpenSettings()}
                                                                            onDelete={(e) =>
                                                                                handleDelete(e, notification.id)
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                    {items.length === 0 &&
                                        (isInitialLoading || isPageLoading || justOpened || !hasLoaded) && (
                                            <NotificationDropdownSkeleton count={6} />
                                        )}
                                    {items.length > 0 && isPageLoading && <NotificationDropdownSkeleton count={2} />}
                                </>
                            )}
                        </div>
                        <div className="rf-notification-dropdown__footer">
                            <Button
                                variant="text"
                                size="sm"
                                tabIndex={detailOpen ? -1 : undefined}
                                onClick={handleViewAll}
                                endAdornment={<MdChevronRight size="18" />}
                            >
                                Все уведомления
                            </Button>
                        </div>
                        {isBusy && (
                            <div className="rf-notification-dropdown__overlay" aria-hidden="true">
                                <span className="rf-notification-dropdown__spinner" />
                            </div>
                        )}
                    </div>

                    {/* ── Пейн «Горизонтальный просмотр» ──────────────────────── */}
                    <div
                        ref={detailPaneRef}
                        className="rf-notification-dropdown__pane rf-notification-dropdown__pane--detail"
                        role="region"
                        aria-label="Просмотр уведомления"
                        aria-hidden={!detailOpen}
                        inert={!detailOpen}
                    >
                        <span
                            className="rf-notification-dropdown__detail-spine"
                            style={detailCat ? ({ '--ndx-accent': detailCat.color } as React.CSSProperties) : undefined}
                            aria-hidden="true"
                        />
                        <div className="rf-notification-dropdown__detail-bar">
                            <button
                                ref={backButtonRef}
                                type="button"
                                className="rf-notification-dropdown__back"
                                onClick={handleBack}
                                aria-label="Назад к списку уведомлений"
                                title="Назад к списку"
                                tabIndex={detailOpen ? undefined : -1}
                            >
                                <MdArrowBack className="rf-notification-dropdown__back-icon" size="20" />
                                <span className="rf-notification-dropdown__back-label">К списку</span>
                            </button>
                            <div className="rf-notification-dropdown__detail-meta">
                                {detailCat && DetailCatIcon && (
                                    <span
                                        className="rf-notification-dropdown__item-cat"
                                        style={{
                                            color: detailCat.color,
                                            background: `color-mix(in srgb, ${detailCat.color} 6%, transparent)`,
                                            border: `1px solid color-mix(in srgb, ${detailCat.color} 26%, transparent)`,
                                        }}
                                    >
                                        <DetailCatIcon size="13" />
                                        {detailCat.label}
                                    </span>
                                )}
                                {active && (
                                    <time
                                        className="rf-notification-dropdown__detail-date"
                                        dateTime={new Date(active.sendedAt).toISOString()}
                                    >
                                        <MdOutlineSchedule size="15" />
                                        <span>{formatFullDate(active.sendedAt)}</span>
                                    </time>
                                )}
                            </div>
                        </div>
                        <div className="rf-notification-dropdown__detail-scroll">
                            <h3 className="rf-notification-dropdown__detail-title">{active?.subject}</h3>
                            <div className="rf-notification-dropdown__detail-divider" aria-hidden="true" />
                            {active && (
                                <NotificationText
                                    className="rf-notification-dropdown__detail-text"
                                    text={active.text}
                                />
                            )}
                        </div>
                        <div className="rf-notification-dropdown__detail-actions">
                            {active && hasBoundDocumentTarget(active) && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    tabIndex={detailOpen ? undefined : -1}
                                    startAdornment={<MdOutlineDescription size="16" />}
                                    onClick={(e) => handleOpenDocument(e, active)}
                                >
                                    Открыть документ
                                </Button>
                            )}
                            {active && detailJobKind !== null && (
                                <JobNotificationActions
                                    key={active.id}
                                    jobId={detailJobId}
                                    kind={detailJobKind}
                                    text={active.text}
                                    tabIndex={detailOpen ? undefined : -1}
                                    onOpenDocument={handleOpenJobNotificationDocument}
                                    onOpenLink={handleOpenJobNotificationLink}
                                />
                            )}
                            {active && hasSupportAccessTarget(active) && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    tabIndex={detailOpen ? undefined : -1}
                                    startAdornment={<MdSupportAgent size="16" />}
                                    onClick={handleOpenSupportAccess}
                                >
                                    Доступ поддержки
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                tabIndex={detailOpen ? undefined : -1}
                                startAdornment={<MdOutlineOpenInNew size="16" />}
                                onClick={handleOpenFull}
                            >
                                Открыть полностью
                            </Button>
                            <span className="rf-notification-dropdown__detail-spacer" />
                            <Button
                                variant="text"
                                size="sm"
                                className="rf-notification-dropdown__detail-delete"
                                tabIndex={detailOpen ? undefined : -1}
                                startAdornment={<MdOutlineDelete size="16" />}
                                onClick={handleDetailDelete}
                            >
                                Удалить
                            </Button>
                        </div>
                    </div>
                </div>
            </AnchoredDialog>
            {snoozeDocKey && (
                <React.Suspense fallback={null}>
                    <MailReminderExecutorModal
                        docId={snoozeDocKey}
                        title="Отложить уведомление"
                        modalSize="xl"
                        actionKey="78cd3077-3c5a-4cd8-b6f5-9b4780026697"
                        okButtonText="ОК"
                        cancelButtonText="Отмена"
                        prefilledText={snoozeText}
                        completeHandler={() => setSnoozeDocKey(null)}
                    />
                </React.Suspense>
            )}
        </>
    );
};

export default NotificationDropdown;
